/**
 * The currency the company reimburses in. Every amount in the workflow — the
 * claimed, reviewed, approved and released figures — is in this currency.
 */
export const HOME_CURRENCY = 'PHP';

/** Philippine peso. Use this for anything in the amount ladder. */
export function money(n: number | null | undefined): string {
  return (
    '₱' +
    (Number(n) || 0).toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

/**
 * A figure in whatever currency a receipt was printed in.
 *
 * Only for showing provenance beside a peso amount — never for the amount
 * ladder, which is pesos throughout.
 */
export function foreignAmount(n: number | null | undefined, currency: string): string {
  const code = (currency || HOME_CURRENCY).toUpperCase();
  if (code === HOME_CURRENCY) return money(n);
  try {
    return (Number(n) || 0).toLocaleString('en-PH', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    // An unrecognised code must still render — a receipt in a currency Intl
    // does not know is a reason to show it plainly, not to throw.
    return `${code} ${(Number(n) || 0).toFixed(2)}`;
  }
}

/**
 * How a converted row is shown wherever it appears: "USD 50.00 @ 58.5000".
 *
 * The rate is always printed, never just the peso result. Finance approving a
 * converted claim needs to see the number it was converted at without opening
 * anything, and an approver who cannot see the rate cannot review the amount.
 */
export function conversionNote(
  originalAmount: number | null | undefined,
  currency: string | null | undefined,
  fxRate: number | null | undefined,
): string | null {
  const code = (currency || HOME_CURRENCY).toUpperCase();
  if (code === HOME_CURRENCY) return null;
  if (!fxRate || !Number.isFinite(fxRate)) return `${foreignAmount(originalAmount, code)} — no rate on file`;
  return `${foreignAmount(originalAmount, code)} @ ${formatRate(fxRate)}`;
}

/** Rates are held to six decimals; trailing zeros are noise on screen. */
export function formatRate(rate: number | null | undefined): string {
  const n = Number(rate) || 0;
  return n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Apply a rate. Rounded to centavos here, once, so the figure stored, the
 * figure totalled and the figure shown are the same number.
 */
export function convertToHome(originalAmount: number, fxRate: number): number {
  return Math.round((Number(originalAmount) || 0) * (Number(fxRate) || 0) * 100) / 100;
}

export function initials(name: string | null | undefined): string {
  return (name || '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function fmtDateTime(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const MONTH_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** "2026-07" -> "July 2026" */
export function periodLabel(period: string): string {
  const { y, m } = periodParts(period);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

export function periodParts(period: string): { y: number; m: number } {
  const [y, m] = period.split('-').map(Number);
  return { y: y ?? 0, m: m ?? 0 };
}

/** Generate the request id for a claim: EXP-2026-00001. */
export function formatRequestId(year: number, sequence: number): string {
  return `EXP-${year}-${String(sequence).padStart(5, '0')}`;
}
